import {useAppContext} from "@/api/app/AppContext"
import {ErrorPage} from "@/components/error/ErrorPage"
import {VersionMismatchBanner} from "@/components/error/VersionMismatchBanner"
import {TokenHint} from "@/components/hint/TokenHint"
import {MobileLayout} from "@/components/layout/MobileLayout"
import Loading from "@/components/Loading"
import React, {useEffect, Suspense} from "react"
import {ErrorBoundary} from "react-error-boundary"
import {Route, Switch, useLocation} from "react-router-dom"
import Landing from "@/pages/mobile/Landing"
import FavIconUpdater from "@/FavIconUpdater"
import TokenInput from "@/pages/mobile/TokenInput"
import ScrollToTop from "@/components/ScrollToTop"
import useOnMount from "@/hooks/useOnMount"
import {cleanRetryParams, handleChunkLoadError, handleVersionMismatchReload} from "@/lib/deploymentRecoveryUtils"
import {loadIdentitySdkModule} from "@/lib/identitySdkLoader"
import {observeGetUserMedia} from "@/lib/cameraFailure"

/**
 * Lazy-loaded journey modules for optimal bundle splitting.
 *
 * These modules are loaded on-demand to prevent income-only users from
 * downloading the identity SDK (sh-live-capture).
 * Vite automatically creates separate bundles for each lazy import.
 */
const IdentityModule = React.lazy(() => import("@/pages/mobile/identity/Identity"))
const IncomeModule = React.lazy(() => import("@/pages/mobile/income/Income"))

const ChunkLoadFallback = () => (
  <MobileLayout>
    <ErrorPage
      message="Unable to load the application. This may be due to a recent update."
      onRetry={() => window.location.reload()}
    />
  </MobileLayout>
)

export const Routes = {
  home: "/",
  identity: "/identity",
  identityStart: "/identity/start",
  identityCapture: "/identity/capture",
  identityFailed: "/identity/failed",
  identityPermissions: "/identity/permissions",
  identityComplete: "/identity/complete",
  identityTimeout: "/identity/timeout",
  identityManualCapture: "/identity/manual-capture",
  identityError: "/identity/error",
  identityCameraLoadError: "/identity/camera-error",

  income: "/income",
  incomeStart: "/income/start",
  incomeUserIdentifiedLanding: "/income/user-identified-landing",
  incomeBankSelection: "/income/bank-selection",
  incomeVerificationMethodChoice: "/income/method-choice",
  incomeManualUploadVerification: "/income/manual-upload",
  incomeOpenBankingExplanation: "/income/secure-statement-sharing-explanation",
  incomeOpenBankingIdentifierSelection: '/income/secure-statement-sharing/identifier-selection',
  incomeAccountSelection: "/income/secure-statement-sharing/account-selection",
  incomeUploadFailed: "/income/upload-failed",
  incomeRequestConsentFailed: "/income/secure-statement-sharing/request-consent-failed",
  incomeComplete: "/income/complete",
  incomeOpenBankingTimeout: "/income/secure-statement-sharing/timeout",
  incomeOpenBankingDenied: "/income/secure-statement-sharing/denied"
}

function MobileApp() {
  const {
    appState,
    onSdkLoadStarted,
    onSdkLoadCompleted,
    onSdkLoadFailed,
    onCdnLoadRetryAttempt,
    onCdnLoadSuccess,
    onCdnLoadFailedAfterRetries,
    onCheckDeploymentVersion
  } = useAppContext()
  const location = useLocation()
  const isIdentityRoute = location.pathname.startsWith('/identity')

  useOnMount(() => {
    onCheckDeploymentVersion()
  })

  useOnMount(cleanRetryParams)

  /**
   * SDK loading happens at the route level, not component level.
   * This prevents Vite bundle optimization conflicts with lazy-loaded modules.
   * Only loads for /identity routes to preserve income journey performance.
   *
   * CDN-based loading via sh-live-capture, resilient at both layers: our fetch of
   * the wrapper module retries (see identitySdkLoader), and the wrapper's own fetch
   * of the IDRND SDK retries internally. If both fail, state triggers the error UI.
   */
  useEffect(() => {
    const preloadIdentitySDK = async () => {
      onSdkLoadStarted()
      const SH_CDN_BASE = appState.tenantConfig?.cdnUrl

      // Before the wrapper module evaluates: the SDK may capture a getUserMedia
      // reference at module scope, which would silently blind the observer
      observeGetUserMedia()

      try {
        const module = await loadIdentitySdkModule(SH_CDN_BASE, {
          onRetry: (info) => onCdnLoadRetryAttempt({...info, source: 'wrapper-module'})
        })

        const SHLiveCaptureReady = module.default
        const loadingStateManager = module.loadingStateManager

        if (loadingStateManager) {
          loadingStateManager.on('retrying', (data) => {
            onCdnLoadRetryAttempt({
              attempt: data.attempt,
              maxAttempts: data.maxAttempts,
              nextRetryDelayMs: data.nextRetryDelayMs,
              lastError: data.lastError?.message || 'Unknown error',
              source: 'idrnd-sdk'
            })
          })

          loadingStateManager.on('failed', (data) => {
            console.error('[sh-live-capture] CDN load failed after all retries:', data.error?.message)
          })
        }

        if (!document.querySelector(`link[href="${SH_CDN_BASE}/sh-live-capture.css"]`)) {
          const cssLink = document.createElement('link')
          cssLink.rel = 'stylesheet'
          cssLink.href = `${SH_CDN_BASE}/sh-live-capture.css`
          document.head.appendChild(cssLink)
        }

        const createSHLiveCapture = await SHLiveCaptureReady
        window.__createSHLiveCapture = createSHLiveCapture

        onSdkLoadCompleted()
        onCdnLoadSuccess()
      } catch (error) {
        onSdkLoadFailed({
          error,
          message: `Identity SDK failed to load from CDN: ${error.message}`
        })
        onCdnLoadFailedAfterRetries({
          error,
          message: error.message
        })
      }
    }

    if (isIdentityRoute && !appState.sdkLoaded && !appState.sdkLoading && !appState.sdkLoadFailed) {
      preloadIdentitySDK().catch(console.error)
    }
  }, [isIdentityRoute, appState.sdkLoaded, appState.sdkLoading, appState.sdkLoadFailed, appState.tenantConfig?.cdnUrl,
    onSdkLoadStarted, onSdkLoadCompleted, onSdkLoadFailed, onCdnLoadRetryAttempt, onCdnLoadSuccess, onCdnLoadFailedAfterRetries])

  /**
   * Route-scoped error handling for SDK failures.
   * Only shows SDK-related errors when user is actually on identity routes.
   * CDN failures are handled with automatic retries (3 attempts), so this only
   * triggers after all retry attempts have been exhausted.
   */
  if (isIdentityRoute && appState.sdkLoadFailed) {
    const token = appState.token
    const tenantName = appState.tenantConfig?.name
    return (
      <MobileLayout>
        <ErrorPage
          message={
            <>
              We are unable to proceed with verification at this time.
              <br/>
              <br/>
              This may be a network error or require additional support.
              <br/>
              Please contact {tenantName}.
            </>
          }
          onRetry={() => window.location.reload()}
          hintMessage={token && <TokenHint token={token}/>}
        />
      </MobileLayout>
    )
  }

  /**
   * Route-scoped loading state for identity SDK.
   * Shows loading only for identity users while SDK downloads.
   * Displays retry progress when sh-live-capture is retrying CDN loads.
   */
  else if (isIdentityRoute && (appState.sdkLoading || !appState.sdkLoaded)) {
    const retryDetails = appState.cdnRetryDetails
    const loadingMessage = retryDetails
      ? `Loading identity verification system... (retrying, attempt ${retryDetails.attempt}/${retryDetails.maxAttempts})`
      : "Loading identity verification system..."

    return <Loading loadingMessage={loadingMessage} withCountdown={false}/>
  }
  /**
   * ErrorBoundary catches chunk 404s after deployments.
   */
  else {
    return (
      <ErrorBoundary fallback={<ChunkLoadFallback/>} onError={handleChunkLoadError}>
        {appState.versionMismatchDetected && (
          <VersionMismatchBanner onReload={handleVersionMismatchReload}/>
        )}
        <ScrollToTop/>
        <Route path={Routes.home} component={FavIconUpdater}/>
        <Switch>
          <Route path={"/t/:token"} exact={true} component={Landing}/>
          <Route path={"/token/:token"} exact={true} component={Landing}/>

          <Route path={Routes.identity} render={() => (
            <Suspense fallback={null}>
              <IdentityModule/>
            </Suspense>
          )}/>

          <Route path={Routes.income} render={() => (
            <Suspense fallback={<Loading loadingMessage="Loading Income service..." withCounter={false}/>}>
              <IncomeModule/>
            </Suspense>
          )}/>

          {/* Fallback to manual token input */}
          <Route component={TokenInput}/>
        </Switch>
      </ErrorBoundary>
    )
  }
}

export default MobileApp